import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { schemesApi, supportingDataApi, faProcessorApi } from '@/services/api';
import { Scheme, SchemeFormData, FASource } from '@/types/client';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import { formatDateTime } from '@/utils/formatters';
import PageCard, { DetailSection, DetailField } from '@/components/utils/PageCard';
import FormCard, {
  FormSection,
  FormGrid,
  FormField,
  FormInput,
  FormSelect,
  FormCheckboxGroup,
  FormCheckbox,
} from '@/components/utils/FormCard';
import { Tooltip } from '@mui/material';
import { PageFrame, Button, ActiveStatus, Note, Placeholder, confirmColors } from '@/components/ui';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Swal from 'sweetalert2';

interface InvestmentStrategy {
  id: number;
  name: string;
}

interface SchemeDetailProps {
  /** Opened from `/schemes/:id/edit` — land straight on the edit form. */
  startInEdit?: boolean;
}

const SchemeDetail: React.FC<SchemeDetailProps> = ({ startInEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [investmentStrategies, setInvestmentStrategies] = useState<InvestmentStrategy[]>([]);
  // FA source options come from the FA processor's source registry — the same keys the
  // backend accepts for Scheme.fa_source, so a newly added bundle needs no change here.
  const [faSources, setFaSources] = useState<FASource[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  const [loadingFaSources, setLoadingFaSources] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(startInEdit);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Scheme>>({});

  useEffect(() => {
    if (id) {
      fetchScheme(parseInt(id));
      fetchInvestmentStrategies();
      fetchFaSources();
    }
  }, [id]);

  const fetchScheme = async (schemeId: number) => {
    try {
      setLoading(true);
      const data = await schemesApi.getScheme(schemeId);
      setScheme(data);
      setEditForm(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch scheme details');
      console.error('Error fetching scheme:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestmentStrategies = async () => {
    try {
      setLoadingStrategies(true);
      const data = await supportingDataApi.getInvestmentStrategies();
      setInvestmentStrategies(data);
    } catch (err) {
      console.error('Failed to load investment strategies:', err);
      // Don't block UI if strategies fail to load
    } finally {
      setLoadingStrategies(false);
    }
  };

  const fetchFaSources = async () => {
    try {
      setLoadingFaSources(true);
      const data = await faProcessorApi.getSources();
      setFaSources(data);
    } catch (err) {
      console.error('Failed to load FA sources:', err);
      // Don't block UI if FA sources fail to load
    } finally {
      setLoadingFaSources(false);
    }
  };

  const handleDelete = async () => {
    if (!scheme) return;

    const result = await Swal.fire({
      title: 'Delete Scheme?',
      text: `Do you want to delete ${scheme.name}? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await schemesApi.deleteScheme(scheme.id);
      navigate('/schemes');
    } catch (err) {
      // Reported by the modal only — the inline Note is for load failures.
      console.error('Error deleting scheme:', err);
      Swal.fire('Error', 'Failed to delete scheme.', 'error');
    }
  };

  const handleEdit = () => {
    setError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (scheme) {
      setEditForm({
        name: scheme.name,
        code: scheme.code || '',
        display_name: scheme.display_name || '',
        acronym: scheme.acronym || '',
        full_name: scheme.full_name || '',
        is_active: scheme.is_active || false,
        fa_scheme_id: scheme.fa_scheme_id,
        investment_strategy: scheme.investment_strategy,
        fa_source: scheme.fa_source ?? null,
        allow_client_wise_orders: Boolean(scheme.allow_client_wise_orders),
      });
    }
    setError(null);
    setIsEditing(false);
    // The edit route has no view mode of its own — hand over to the detail route.
    if (startInEdit) navigate(`/schemes/${id}`, { replace: true });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheme) return;

    try {
      setSaving(true);
      const updatedScheme = await schemesApi.updateScheme(scheme.id, editForm as SchemeFormData);
      setScheme(updatedScheme);
      setEditForm(updatedScheme);
      setIsEditing(false);
      setError(null);
      if (startInEdit) navigate(`/schemes/${id}`, { replace: true });
    } catch (err) {
      setError('Failed to update scheme');
      console.error('Error updating scheme:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = <K extends keyof SchemeFormData>(field: K, value: SchemeFormData[K]) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <PageFrame header={null}>
        <Placeholder kind="loading" />
      </PageFrame>
    );
  }

  if (error && !scheme) {
    return (
      <PageFrame header={null}>
        <Note tone="error">{error}</Note>
      </PageFrame>
    );
  }

  if (!scheme) {
    return (
      <PageFrame header={null}>
        <Note tone="warning">Scheme not found.</Note>
      </PageFrame>
    );
  }

  if (isEditing) {
    return (
      <FormCard
        title={`Edit ${scheme.name}`}
        subtitle="Changes apply as soon as you save."
        variant="page"
        errorBanner={error || undefined}
        onSubmit={handleSave}
        onCancel={handleCancelEdit}
        submitLabel={saving ? 'Saving…' : 'Save Changes'}
        submitting={saving}
        onBack={handleCancelEdit}
        backLabel="← Back to details"
      >
        <FormSection title="Basic Information">
          <FormGrid>
            <FormField label="Name" required>
              <FormInput
                type="text"
                value={editForm.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                disabled={saving}
              />
            </FormField>

            <FormField label="Code">
              <FormInput
                type="text"
                value={editForm.code || ''}
                onChange={(e) => handleInputChange('code', e.target.value)}
                disabled={saving}
              />
            </FormField>

            <FormField label="Display Name">
              <FormInput
                type="text"
                value={editForm.display_name || ''}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                disabled={saving}
              />
            </FormField>

            <FormField label="Acronym">
              <FormInput
                type="text"
                value={editForm.acronym || ''}
                onChange={(e) => handleInputChange('acronym', e.target.value)}
                placeholder="e.g., CAP"
                disabled={saving}
              />
            </FormField>

            <FormField label="Full Name">
              <FormInput
                type="text"
                value={editForm.full_name || ''}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                placeholder="e.g., Capital Appreciation Fund"
                disabled={saving}
              />
            </FormField>

            <FormField label="Investment Strategy">
              <FormSelect
                value={editForm.investment_strategy || ''}
                onChange={(e) =>
                  handleInputChange('investment_strategy', e.target.value ? Number(e.target.value) : undefined)
                }
                disabled={loadingStrategies || saving}
              >
                <option value="">-- Select Strategy --</option>
                {investmentStrategies.map((strat) => (
                  <option key={strat.id} value={strat.id}>
                    {strat.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection
          title="Fund Accounting"
          description="FA Source decides which daily fund-accounting bundle refreshes this scheme's holdings. Leave it unset and the daily FA upload never touches it."
        >
          <FormGrid>
            <FormField label="FA Scheme ID">
              <FormInput
                type="number"
                value={editForm.fa_scheme_id ?? ''}
                onChange={(e) => handleInputChange('fa_scheme_id', e.target.value ? Number(e.target.value) : undefined)}
                disabled={saving}
              />
            </FormField>

            <FormField label="FA Source">
              <FormSelect
                value={editForm.fa_source ?? ''}
                onChange={(e) => handleInputChange('fa_source', e.target.value || null)}
                disabled={loadingFaSources || saving}
              >
                <option value="">-- Not loaded by FA upload --</option>
                {faSources.map((src) => (
                  <option key={src.value} value={src.value}>
                    {src.label}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </FormGrid>
        </FormSection>

        <FormSection title="Order Generation">
          <FormCheckboxGroup>
            <FormCheckbox
              label="Allow client-wise orders"
              hint="Buy and sell orders can be generated for selected clients of this scheme"
              checked={Boolean(editForm.allow_client_wise_orders)}
              onChange={(e) => handleInputChange('allow_client_wise_orders', e.target.checked)}
              disabled={saving}
            />
          </FormCheckboxGroup>
        </FormSection>

        <FormSection title="Status">
          <FormCheckboxGroup>
            <FormCheckbox
              label="Is Active"
              hint="Inactive schemes are hidden from order flows"
              checked={Boolean(editForm.is_active)}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              disabled={saving}
            />
          </FormCheckboxGroup>
        </FormSection>
      </FormCard>
    );
  }

  const actions = (
    <>
      <Tooltip title="Back to list">
        <Button
          iconOnly
          onClick={() => navigate('/schemes')}
          aria-label="Back to list"
        >
          <ArrowBackIcon fontSize="small" />
        </Button>
      </Tooltip>

      {canWrite && (
        <Tooltip title="Edit">
          <Button
            iconOnly
            onClick={handleEdit}
            aria-label="Edit"
          >
            <EditIcon fontSize="small" />
          </Button>
        </Tooltip>
      )}

      {canWrite && (
        <Button
          variant="danger"
          icon={<DeleteIcon fontSize="small" />}
          onClick={handleDelete}
          size="sm"
        >
          Delete
        </Button>
      )}
    </>
  );

  return (
    <PageCard
      title={scheme.code ? `${scheme.code} — ${scheme.name}` : scheme.name}
      subtitle={<ActiveStatus active={!!scheme.is_active} />}
      actions={actions}
    >
      <DetailSection title="Basic Information">
        <DetailField label="Name" value={scheme.name} />
        <DetailField label="Code" value={scheme.code} mono />
        <DetailField label="Display Name" value={scheme.display_name} />
        <DetailField label="Acronym" value={scheme.acronym} mono />
        <DetailField label="Full Name" value={scheme.full_name} />
        <DetailField
          label="Investment Strategy"
          value={scheme.investment_strategy_display || scheme.investment_strategy}
        />
      </DetailSection>

      {/*
        FA Source decides which daily fund-accounting bundle refreshes this
        scheme's holdings. Unset means the daily FA upload never touches it.
      */}
      <DetailSection title="Fund Accounting">
        <DetailField label="FA Scheme ID" value={scheme.fa_scheme_id} mono />
        <DetailField
          label="FA Source"
          value={scheme.fa_source_display || scheme.fa_source || 'Not loaded by FA upload'}
        />
      </DetailSection>

      <DetailSection title="Order Generation">
        <DetailField
          label="Client-wise Orders"
          value={scheme.allow_client_wise_orders ? 'Allowed' : 'Not allowed'}
        />
      </DetailSection>

      <DetailSection title="Audit Information">
        <DetailField label="Created Date" value={formatDateTime(scheme.created_ts)} />
        <DetailField label="Created By ID" value={scheme.created_by} />
        <DetailField
          label="Last Updated"
          value={scheme.updated_ts ? formatDateTime(scheme.updated_ts) : undefined}
        />
        <DetailField label="Updated By ID" value={scheme.updated_by} />
      </DetailSection>
    </PageCard>
  );
};

export default SchemeDetail;
